import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "../../../Layout/index.jsx";
import NetworkIndicator from "../../../../Practice/Components/NetworkIndicator.jsx";
import GuardedActionButton from "../../../../Practice/Components/GuardedActionButton.jsx";
import {
  Button,
  Checkbox,
  Label,
  Select,
  TextInput,
  Textarea,
  Tooltip,
} from "flowbite-react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "../../../../../toast";
import { IoMdArrowRoundBack, IoMdAdd } from "react-icons/io";
import { MdDelete } from "react-icons/md";
import { Editor } from "@tinymce/tinymce-react";
import { WithContext as ReactTags } from "react-tag-input";
import { v4 as uuidv4 } from "uuid";
import { FaCircleInfo } from "react-icons/fa6";
import {
  cppTooltipContent,
  cTooltipContent,
  javaTooltipContent,
  pythonTooltipContent,
} from "../../../Helpers/TooltipData.jsx";
import {
  useCreateQuestionMutation,
  useGetCourseQuery,
} from "../../../../../graphql/generated.tsx";
import ConformationModal from "../../../Components/ConformationModal.jsx";
import { useBlocker } from "../../../Helpers/Blocker.jsx";

/**
 * CreateQuestion.jsx
 *
 * Full updated component with:
 *  - autosave to localStorage
 *  - restore from localStorage on mount
 *  - beforeunload protection (native browser dialog)
 *  - confirmation modal on back/navigation when unsaved changes exist
 *  - clearing localStorage on successful submit
 */

// Key used to save the draft in localStorage. You can make this dynamic per-course if required.
const STORAGE_KEY = "createQuestionDraft_v1";

const CreateQuestion = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Form state
  const [text, setText] = useState("");
  const [testCases, setTestCases] = useState([]);
  const [hints, setHints] = useState([""]);
  const [isValid, setIsValid] = useState(false);
  const [courseData, setCourseData] = useState(null);
  const [category, setCategory] = useState({ id: "", name: "" });
  const [title, setTitle] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [tags, setTags] = useState([]);
  const [companyTags, setCompanyTags] = useState([]);
  const [usePredefinedCode, setUsePredefinedCode] = useState(false);
  const [predefinedCode, setPredefinedCode] = useState({
    python: "",
    c: "",
    java: "",
    cpp: "",
  });
  const [isCreateDisabled, setIsCreateDisabled] = useState(false);
  const [showFunctionOnly, setShowFunctionOnly] = useState(false);
  const [targetFunctionName, setTargetFunctionName] = useState("");
  const [openModal, setOpenModal] = useState(false); // confirmation modal for leaving
  const [restoreToastShown, setRestoreToastShown] = useState(false);

  // GraphQL hooks
  const [createQuestion, { loading: creating }] = useCreateQuestionMutation();
  const { data } = useGetCourseQuery({ variables: { id } });

  // Editor config
  const editorConfig = {
    height: 300,
    menubar: false,
    plugins: [
      "advlist",
      "autolink",
      "lists",
      "link",
      "image",
      "charmap",
      "preview",
      "anchor",
      "searchreplace",
      "visualblocks",
      "code",
      "fullscreen",
      "insertdatetime",
      "media",
      "table",
      "help",
      "wordcount",
    ],
    directionality: "ltr",
    toolbar:
      "undo redo | blocks | " +
      "bold italic forecolor | alignleft aligncenter " +
      "alignright alignjustify | bullist numlist outdent indent | " +
      "removeformat | help",
    content_style:
      "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
    branding: false,
  };

  // Helper: compute total marks
  const computeTotalMarks = useCallback(() => {
    return testCases.reduce(
      (total, test) => total + (parseFloat(test.weightage) || 0),
      0
    );
  }, [testCases]);

  // Load course data into local state when query completes
  useEffect(() => {
    setCourseData(data?.getCourse);
  }, [data]);

  const predefinedCodeLanguages = courseData?.languages || [];

  // ---- Autosave / Restore logic ----

  // Load saved draft from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);

        // Restore only if parsed is an object
        if (parsed && typeof parsed === "object") {
          setText(parsed.text || "");
          setTitle(parsed.title || "");
          setCategory(parsed.category || { id: "", name: "" });
          setDifficulty(parsed.difficulty || "");
          setTestCases(parsed.testCases || []);
          setHints(parsed.hints || [""]);
          setTags(parsed.tags || []);
          setCompanyTags(parsed.companyTags || []);
          setUsePredefinedCode(parsed.usePredefinedCode || false);
          setPredefinedCode(parsed.predefinedCode || { python: "", c: "", java: "", cpp: "" });
          setShowFunctionOnly(parsed.showFunctionOnly || false);
          setTargetFunctionName(parsed.targetFunctionName || "");
          if (!restoreToastShown) {
            toast.info("Restored unsaved draft from previous session.");
            setRestoreToastShown(true);
          }
        }
      }
    } catch (err) {
      console.error("Error parsing saved draft:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save draft to localStorage whenever key parts of the form change
  useEffect(() => {
    try {
      const draft = {
        text,
        title,
        category,
        difficulty,
        testCases,
        hints,
        tags,
        companyTags,
        usePredefinedCode,
        predefinedCode,
        showFunctionOnly,
        targetFunctionName,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    } catch (err) {
      console.error("Error saving draft:", err);
    }
  }, [
    text,
    title,
    category,
    difficulty,
    testCases,
    hints,
    tags,
    companyTags,
    usePredefinedCode,
    predefinedCode,
    showFunctionOnly,
    targetFunctionName,
  ]);

  // Remove saved draft
  const clearDraft = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.error("Error clearing draft:", err);
    }
  };

  // ---- Unsaved changes check & navigation blocking ----

  const hasUnsavedChanges = () =>
    !!(
      (text && text.trim().length > 0) ||
      (title && title.trim().length > 0) ||
      (testCases && testCases.length > 0) ||
      (hints && hints.some((h) => h && h.trim().length > 0)) ||
      (tags && tags.length > 0) ||
      (companyTags && companyTags.length > 0) ||
      (predefinedCode &&
        (predefinedCode.python ||
          predefinedCode.c ||
          predefinedCode.java ||
          predefinedCode.cpp))
    );

  // Use your custom blocker hook - call exactly as you did earlier

    useBlocker((tx) => {
    if (hasUnsavedChanges()) {
      const confirmLeave = window.confirm(
        "You have unsaved changes. Are you sure you want to leave this page? Your draft has been saved locally."
      );
      // if (confirmLeave) tx.retry();
    } else {
      // tx.retry();
    }
  }, hasUnsavedChanges());

  // Native beforeunload handler for reload/close
  useEffect(() => {
    const beforeUnloadHandler = (event) => {
      if (hasUnsavedChanges()) {
        event.preventDefault();
        // Chrome requires returnValue to be set
        event.returnValue = "";
        return "";
      }
      return undefined;
    };

    window.addEventListener("beforeunload", beforeUnloadHandler);

    return () => {
      window.removeEventListener("beforeunload", beforeUnloadHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    text,
    title,
    testCases,
    hints,
    tags,
    companyTags,
    predefinedCode,
    showFunctionOnly,
    targetFunctionName,
  ]);

  // ---- Test case handlers ----

  const handleAddTestCase = () => {
    const newTestCase = {
      id: uuidv4(),
      visibility: "SAMPLE",
      input: "",
      output: "",
      weightage: 0,
    };
    setTestCases((prev) => [...prev, newTestCase]);
  };

  const handleRemoveTestCase = (id) => {
    setTestCases((prev) => prev.filter((tc) => tc.id !== id));
  };

  const handleTestCaseChange = (id, field, value) => {
    setTestCases((prev) =>
      prev.map((tc) => (tc.id === id ? { ...tc, [field]: value } : tc))
    );
  };

  // ---- Hints handlers ----
  const handleAddHint = () => {
    setHints((prev) => [...prev, ""]);
  };

  const handleRemoveHint = (index) => {
    setHints((prev) => prev.filter((_, i) => i !== index));
  };

  const handleHintChange = (index, value) => {
    setHints((prev) => {
      const copy = [...prev];
      copy[index] = value;
      return copy;
    });
  };

  // ---- Tag handlers ----
  const handleDelete = (i) => {
    const newTags = tags.slice(0);
    newTags.splice(i, 1);
    setTags(newTags);
  };

  const handleAddition = (tag) => {
    setTags([...tags, tag]);
  };

  const handleCompanyTagDelete = (i) => {
    const newCompanyTags = [...companyTags];
    newCompanyTags.splice(i, 1);
    setCompanyTags(newCompanyTags);
  };

  const handleCompanyTagAddition = (tag) => {
    setCompanyTags([...companyTags, tag]);
  };

  const KeyCodes = {
    comma: 188,
    enter: 13,
  };

  const delimiters = [KeyCodes.comma, KeyCodes.enter];

  // ---- Form validation ----
  const validateForm = () => {
    const isTextValid = text?.trim() !== "";
    const areTestCasesValid =
      testCases.length > 0 &&
      testCases.every(
        (testCase) =>
          typeof testCase.visibility !== "undefined" &&
          testCase.input &&
          String(testCase.input).trim() !== "" &&
          testCase.output &&
          String(testCase.output).trim() !== ""
      );

    setIsValid(isTextValid && areTestCasesValid);
  };

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, testCases]);

  // ---- Submit handler ----
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    // Basic validation - mirror your original checks
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!text.trim()) {
      toast.error("Question text is required");
      return;
    }
    if (!category?.id) {
      toast.error("Category is required");
      return;
    }
    if (!difficulty) {
      toast.error("Difficulty level is required");
      return;
    }
    if (testCases.length === 0) {
      toast.error("At least one test case is required");
      return;
    }
    for (let testCase of testCases) {
      if (!String(testCase.input).trim() || !String(testCase.output).trim()) {
        toast.error("Test case input and output cannot be empty");
        return;
      }
    }

    const totalMarks = computeTotalMarks();

    if (difficulty === "EASY" && (totalMarks < 30 || totalMarks > 60)) {
      toast.error("Invalid Total Mark for EASY. Allowed range: 30-60");
      return;
    }
    if (difficulty === "MEDIUM" && (totalMarks < 50 || totalMarks > 100)) {
      toast.error("Invalid Total Mark for MEDIUM. Allowed range: 50-100");
      return;
    }
    if (difficulty === "HARD" && (totalMarks < 70 || totalMarks > 150)) {
      toast.error("Invalid Total Mark for HARD. Allowed range: 70-150");
      return;
    }

    if (usePredefinedCode) {
      if (predefinedCodeLanguages.includes("PYTHON") && !predefinedCode.python.trim()) {
        toast.error("Predefined Python code cannot be empty");
        return;
      }
      if (predefinedCodeLanguages.includes("C") && !predefinedCode.c.trim()) {
        toast.error("Predefined C code cannot be empty");
        return;
      }
      if (predefinedCodeLanguages.includes("JAVA") && !predefinedCode.java.trim()) {
        toast.error("Predefined JAVA code cannot be empty");
        return;
      }
      if (predefinedCodeLanguages.includes("CPP") && !predefinedCode.cpp.trim()) {
        toast.error("Predefined C++ code cannot be empty");
        return;
      }
    }
    
    const user = JSON.parse(localStorage.getItem("user"));
    const userdisplayName = user?.displayName;

    // Build request body (mirror your original request structure)
    const requestBody = {
      course_id: id,
      title,
      text,
      category,
      test_cases: testCases.map((test) => ({
        id: test.id,
        input: test.input,
        output: test.output,
        visibility: test.visibility,
        weightage: parseFloat(test.weightage) || 0,
      })),
      marks: String(totalMarks),
      hints,
      difficulty,
      tags: tags.map((t) => t.text || t),
      company_tags: companyTags.map((t) => t.text || t),
      predefinedCode: usePredefinedCode ? predefinedCode : null,
      showFunctionOnly,
      targetFunctionName,
      createdBy: userdisplayName
    };

    try {
  let result = await createQuestion({
    variables: { createQuestionInput: requestBody }
  });

  const courseId = result?.data?.createQuestion?.course_id;
  const questionId = result?.data?.createQuestion?.id;


  // Clear draft after success
  clearDraft();

  // Navigate to course page in current tab
  navigate(`/admin/courses/${id}`);

  // Open preview page in a new tab
  window.open(
    `/admin/courses/preview-question/${courseId}/${questionId}`,
    "_blank"
  );

} catch (error) {
  console.error("Create question error:", error);
  const errorMessage = error?.message || "Error In Creating Question";
  toast.error(`Error: ${errorMessage}`);
}


  };

  // Wrapper to call submit without an event (used by GuardedActionButton)
    const handleCreateClick = async () => {
        if (isCreateDisabled) return; 
        setIsCreateDisabled(true);

        await handleSubmit({ preventDefault: () => {} });

        // Re-enable button only if error occurred
        setIsCreateDisabled(false);
      };


  // Back button handler: if unsaved changes => show modal, else navigate back
  const handleBackClick = () => {
    if (hasUnsavedChanges()) {
      setOpenModal(true);
    } else {
      navigate(-1);
    }
  };

  // JSX render
  return (
    <AdminLayout>
      <NetworkIndicator />
      <div className="px-10 py-8 mx-auto max-md:px-5">
        <div className="flex items-center mb-6">
          <button
            onClick={handleBackClick}
            className="text-gray-500 hover:text-gray-700 focus:outline-none w-[100px]"
          >
            <span className="flex items-center gap-2 text-sm font-medium">
              <IoMdArrowRoundBack className="w-6 h-6" />
              Go Back
            </span>
          </button>
        </div>

        <h2 className="w-full mb-4 text-3xl font-bold text-center max-md:text-xl">
          Create Question
        </h2>

        <form
          onSubmit={handleSubmit}
          className="max-w-2xl p-8 mx-auto bg-white rounded-md shadow-md max-md:p-6"
        >
          {/* Title */}
          <div className="mb-5">
            <div className="block">
              <Label htmlFor="title" value="Title" className="font-semibold" />
            </div>
            <TextInput
              required
              id="title"
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                validateForm();
              }}
            />
          </div>

          {/* Editor */}
          <div className="mb-5">
            <div className="block ">
              <Label
                htmlFor="questionText"
                value="Question Text"
                className="font-semibold"
              />
            </div>
            <Editor
              apiKey={"oq48u1edly4k9weyi67f8gldw5v0t7szr1b8bp1727x82zji"}
              initialValue={""}
              init={editorConfig}
              onEditorChange={(content) => setText(content)}
            />
          </div>

          {/* Difficulty */}
          <div className="mb-5">
            <div className="block">
              <Label
                htmlFor="difficulty"
                value="Difficulty Level"
                className="font-semibold"
              />
            </div>
            <Select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              required
            >
              <option value="" disabled>
                Select Difficulty
              </option>
              <option value="EASY">Easy</option>
              <option value="MEDIUM">Medium</option>
              <option value="HARD">Hard</option>
            </Select>
          </div>

          {/* Category */}
          <div className="mb-5">
            <div className="block">
              <Label
                htmlFor="category"
                value="Category"
                className="font-semibold"
              />
            </div>
            <Select
              id="category"
              value={category.name || ""}
              onChange={(e) => {
                const selectedCategory = courseData?.categories?.find(
                  (cat) => cat.name === e.target.value || cat._id === e.target.value
                );
                if (selectedCategory) {
                  setCategory({ id: selectedCategory.id || selectedCategory._id || "", name: selectedCategory.name });
                } else {
                  setCategory({ id: "", name: e.target.value });
                }
              }}
              required
              className=""
            >
              <option value="">Select Category</option>
              {courseData?.categories?.map((cat) => (
                <option key={cat._id || cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </Select>
          </div>

          {/* Test Cases */}
          <div className="block">
            <Label
              htmlFor="testCases"
              value="Test Cases"
              className="mb-2 font-semibold"
            />
          </div>

          {testCases.map((testCase) => (
            <div key={testCase.id} className="p-6 mb-4 border rounded-md ">
              <Select
                value={testCase.visibility}
                onChange={(e) =>
                  handleTestCaseChange(testCase.id, "visibility", e.target.value)
                }
                required
                className="mb-3"
              >
                <option value="SAMPLE">Sample</option>
                <option value="HIDDEN">Hidden</option>
              </Select>

              <Textarea
                required
                type="text"
                className="mb-3"
                placeholder="Input"
                value={testCase.input}
                onChange={(e) =>
                  handleTestCaseChange(testCase.id, "input", e.target.value)
                }
              />
              <Textarea
                required
                type="text"
                className="mb-3"
                placeholder="Output"
                value={testCase.output}
                onChange={(e) =>
                  handleTestCaseChange(testCase.id, "output", e.target.value)
                }
              />
              <TextInput
                required
                type="number"
                className="mb-3"
                placeholder="Weightage"
                value={testCase.weightage}
                onChange={(e) =>
                  handleTestCaseChange(testCase.id, "weightage", e.target.value)
                }
              />
              <MdDelete
                className="w-8 h-6 m-2 text-gray-500 cursor-pointer hover:text-red-500"
                onClick={() => handleRemoveTestCase(testCase.id)}
              />
            </div>
          ))}

          <Button
            type="button"
            className="flex items-center mb-5 h-9 bg-primary"
            onClick={handleAddTestCase}
          >
            <IoMdAdd className="p-0 mt-1 mr-1" /> <span>Add testcase</span>
          </Button>

          {/* Hints */}
          <div className="block mb-2">
            <Label htmlFor="hints" value="Hints" className="font-semibold" />
          </div>
          {hints.map((hint, index) => (
            <div key={index} className="flex">
              <TextInput
                type="text"
                className="w-full mb-5"
                placeholder={`Hint ${index + 1}`}
                value={hint}
                onChange={(e) => handleHintChange(index, e.target.value)}
              />
              <MdDelete
                className="w-8 h-6 m-2 text-gray-500 cursor-pointer hover:text-red-500"
                onClick={() => handleRemoveHint(index)}
              />
            </div>
          ))}

          <Button
            type="button"
            className="flex items-center mb-5 h-9 bg-primary"
            onClick={handleAddHint}
          >
            <IoMdAdd className="mt-1 mr-1" /> <span>Add hint</span>
          </Button>

          {/* Marks (computed) */}
          <div className="block">
            <Label htmlFor="marks" value="Marks" className="font-semibold" />
          </div>
          <TextInput
            required
            type="number"
            className="mb-5"
            disabled
            value={computeTotalMarks()}
          />

          {/* Tags */}
          <div className="mb-5">
            <div className="block">
              <Label htmlFor="tags" value="Tags" className="font-semibold" />
            </div>
            <ReactTags
              inline
              tags={tags}
              delimiters={delimiters}
              handleDelete={handleDelete}
              handleAddition={handleAddition}
              autocomplete
              classNames={{
                root: "flex flex-wrap",
                rootFocused: "border-blue-500",
                selected: "text-black rounded-full mt-2",
                tag: "bg-primary text-white rounded-xl py-1 px-2 cursor-pointer m-1",
                tagInput: " p-2 rounded",
                inputField: "p-2 rounded bg-blue-100",
                suggestions: "mt-1 p-1 bg-white border border-gray-300 rounded",
                suggestionActive: "bg-gray-100",
                remove: "text-red-500 ml-2 font-bold",
              }}
            />
          </div>

          {/* Company Tags */}
          <div className="w-full mb-5">
            <div className="block">
              <Label
                htmlFor="companyTags"
                value="Company Tags"
                className="font-semibold"
              />
            </div>
            <ReactTags
              inline
              tags={companyTags}
              delimiters={delimiters}
              handleDelete={handleCompanyTagDelete}
              handleAddition={handleCompanyTagAddition}
              autocomplete
              classNames={{
                root: "flex flex-wrap",
                rootFocused: "border-blue-500",
                selected: "text-gray-500 rounded-full mt-2",
                tag: "bg-primary text-gray-200 rounded-xl py-1 px-2 cursor-pointer m-1",
                tagInput: "w-full p-2 rounded",
                inputField: "w-full p-2 rounded",
                suggestions: "mt-1 p-1 bg-white border border-gray-300 rounded",
                suggestionActive: "bg-gray-100",
                remove: "text-red-500 ml-2 font-bold",
                placeholder: "Enter the tags",
              }}
            />
          </div>

          {/* Predefined Code */}
          <div className="mb-5">
            <Checkbox
              id="usePredefinedCode"
              checked={usePredefinedCode}
              onChange={(e) => setUsePredefinedCode(e.target.checked)}
            />
            <Label
              htmlFor="usePredefinedCode"
              value="Use Predefined Code"
              className="ml-4"
            />
          </div>

          {usePredefinedCode && (
            <>
              {predefinedCodeLanguages.includes("PYTHON") && (
                <div className="mb-5">
                  <div className="flex items-center">
                    <Label
                      htmlFor="predefinedCodePython"
                      value="Predefined Code (Python)"
                      className="font-semibold"
                    />
                    <Tooltip
                      content={
                        <div
                          dangerouslySetInnerHTML={{
                            __html: pythonTooltipContent,
                          }}
                        />
                      }
                      className="h-[150px]"
                      placement="right"
                      style="light"
                    >
                      <FaCircleInfo className="ml-2" color="gray" />
                    </Tooltip>
                  </div>
                  <Textarea
                    id="predefinedCodePython"
                    value={predefinedCode.python}
                    onChange={(e) =>
                      setPredefinedCode((prev) => ({ ...prev, python: e.target.value }))
                    }
                    placeholder="Add predefined code for Python..."
                  />
                </div>
              )}

              {predefinedCodeLanguages.includes("C") && (
                <div className="mb-5">
                  <div className="flex items-center">
                    <Label
                      htmlFor="predefinedCodeC"
                      value="Predefined Code (C)"
                      className="font-semibold"
                    />
                    <Tooltip
                      content={
                        <div
                          dangerouslySetInnerHTML={{
                            __html: cTooltipContent,
                          }}
                        />
                      }
                      className="h-[150px]"
                      placement="right"
                      style="light"
                    >
                      <FaCircleInfo className="ml-2" color="gray" />
                    </Tooltip>
                  </div>
                  <Textarea
                    id="predefinedCodeC"
                    value={predefinedCode.c}
                    onChange={(e) =>
                      setPredefinedCode((prev) => ({ ...prev, c: e.target.value }))
                    }
                    placeholder="Add predefined code for C..."
                  />
                </div>
              )}

              {predefinedCodeLanguages.includes("JAVA") && (
                <div className="mb-5">
                  <div className="flex items-center">
                    <Label
                      htmlFor="predefinedCodeJava"
                      value="Predefined Code (Java)"
                      className="font-semibold"
                    />
                    <Tooltip
                      content={
                        <div
                          dangerouslySetInnerHTML={{
                            __html: javaTooltipContent,
                          }}
                        />
                      }
                      className="h-[150px]"
                      placement="right"
                      style="light"
                    >
                      <FaCircleInfo className="ml-2" color="gray" />
                    </Tooltip>
                  </div>
                  <Textarea
                    id="predefinedCodeJava"
                    value={predefinedCode.java}
                    onChange={(e) =>
                      setPredefinedCode((prev) => ({ ...prev, java: e.target.value }))
                    }
                    placeholder="Add predefined code for Java..."
                  />
                </div>
              )}

              {predefinedCodeLanguages.includes("CPP") && (
                <div className="mb-5">
                  <div className="flex items-center">
                    <Label
                      htmlFor="predefinedCodeCPP"
                      value="Predefined Code (C++)"
                      className="font-semibold"
                    />
                    <Tooltip
                      content={
                        <div
                          dangerouslySetInnerHTML={{
                            __html: cppTooltipContent,
                          }}
                        />
                      }
                      className="h-[150px]"
                      placement="right"
                      style="light"
                    >
                      <FaCircleInfo className="ml-2" color="gray" />
                    </Tooltip>
                  </div>
                  <Textarea
                    id="predefinedCodeCPP"
                    value={predefinedCode.cpp}
                    onChange={(e) =>
                      setPredefinedCode((prev) => ({ ...prev, cpp: e.target.value }))
                    }
                    placeholder="Add predefined code for C++..."
                  />
                </div>
              )}

              <div className="mb-5">
                <Checkbox
                  id="showFunctionOnly"
                  checked={showFunctionOnly}
                  onChange={(e) => setShowFunctionOnly(e.target.checked)}
                />
                <Label
                  htmlFor="showFunctionOnly"
                  value="Show Function Only"
                  className="ml-4"
                />
              </div>

              {showFunctionOnly && (
                <div className="mb-5">
                  <Label
                    htmlFor="targetFunctionName"
                    value="Target Function Name"
                    className="font-semibold"
                  />
                  <TextInput
                    id="targetFunctionName"
                    value={targetFunctionName}
                    onChange={(e) => setTargetFunctionName(e.target.value)}
                    placeholder="Enter the target function name..."
                  />
                </div>
              )}
            </>
          )}

          {/* Guarded Create Button */}
          <GuardedActionButton
            className="self-center p-0 bg-primary"
            label="Create"
            onAction={handleCreateClick}
            loading={creating}
            spinnerOnChecking={false}
            disabled={isCreateDisabled || creating}
          />
        </form>
      </div>

      {/* Confirmation Modal: used when trying to navigate back while there are unsaved changes */}
      <ConformationModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        event={() => {
          // If user confirms leaving, navigate back. The draft is preserved in localStorage if you want it retained.
          // If you'd prefer to wipe the draft on confirm, uncomment clearDraft() here.
          // clearDraft();
          navigate(-1);
        }}
        text="You have unsaved changes. Are you sure you want to leave without saving? Your draft has been saved locally."
      />
    </AdminLayout>
  );
};

export default CreateQuestion;
